import type { CodeBrand } from '@/config/codeBrand';

/** localStorage for /7_1 — how respondent first discovered the product (single choice). */
export const Q7_1_STORAGE_KEY = 'npdsurvey:q7_1';

/** LBS option labels (canonical stored values). */
export const Q7_1_OPTIONS_LBS = [
  'Seen it at a store/outlet',
  'Heard about it from someone i know',
  'Seen it online',
  'Seen the pack/product before',
  "Don't remember",
] as const;

/** Dice option labels (canonical stored values). */
export const Q7_1_OPTIONS_DICE = [
  'Seen it at a store/outlet',
  'Heard about it from someone I know',
  'Seen it online',
  'Seen the pack/product before',
  "Don't remember",
] as const;

/** @deprecated Prefer brand-specific lists via `getQ7_1Options`. */
export const Q7_1_OPTIONS = Q7_1_OPTIONS_LBS;

export type Q7_1OptionLbs = (typeof Q7_1_OPTIONS_LBS)[number];
export type Q7_1OptionDice = (typeof Q7_1_OPTIONS_DICE)[number];
export type Q7_1Option = Q7_1OptionLbs | Q7_1OptionDice;

export type Q7_1Answers = {
  /** Single selected option label (kept as one-element array for API shape). */
  selectedOutlets: string[];
};

export function getQ7_1Options(brand: CodeBrand | null | undefined): readonly Q7_1Option[] {
  return brand === 'Dice' ? Q7_1_OPTIONS_DICE : Q7_1_OPTIONS_LBS;
}

function isQ7_1Option(value: string): value is Q7_1Option {
  return (
    (Q7_1_OPTIONS_LBS as readonly string[]).includes(value) ||
    (Q7_1_OPTIONS_DICE as readonly string[]).includes(value)
  );
}

export function readQ7_1Answers(): Q7_1Answers | null {
  try {
    const raw = localStorage.getItem(Q7_1_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as unknown;
    if (typeof o !== 'object' || o === null) return null;
    const sel = (o as { selectedOutlets?: unknown }).selectedOutlets;
    if (!Array.isArray(sel)) return null;
    const selectedOutlets = sel
      .filter((x): x is string => typeof x === 'string')
      .filter(isQ7_1Option);
    if (selectedOutlets.length === 0) return null;
    return { selectedOutlets: [selectedOutlets[0]] };
  } catch {
    return null;
  }
}

export function writeQ7_1Answers(data: Q7_1Answers): void {
  try {
    const first = data.selectedOutlets.find(isQ7_1Option);
    localStorage.setItem(
      Q7_1_STORAGE_KEY,
      JSON.stringify({ selectedOutlets: first ? [first] : [] })
    );
  } catch {
    /* ignore */
  }
}

export function clearQ7_1Answers(): void {
  try {
    localStorage.removeItem(Q7_1_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function isQ7_1Complete(): boolean {
  const a = readQ7_1Answers();
  return a != null && a.selectedOutlets.length === 1;
}
